import React, { useState } from 'react';
import {
  Dialog,
  DialogContent,
  Box,
  Typography,
  IconButton,
  Fade,
  useTheme,
  useMediaQuery
} from '@mui/material';
import { Close } from '@mui/icons-material';
import SignInForm from './SignInForm.tsx';
import SignUpForm from './SignUpForm.tsx';
import ConfirmSignUpForm from './ConfirmSignUpForm.tsx';
import { useAuth } from '../../contexts/AuthContext.tsx';

export type AuthModalView = 'signin' | 'signup' | 'confirm-signup';

interface AuthModalProps {
  open: boolean;
  onClose: () => void;
  initialView?: AuthModalView;
}

const AuthModal: React.FC<AuthModalProps> = ({
  open,
  onClose,
  initialView = 'signin'
}) => {
  const { clearError } = useAuth();
  const [currentView, setCurrentView] = useState<AuthModalView>(initialView);
  const [signupData, setSignupData] = useState<{
    email: string;
    password: string;
    firstName: string;
  }>({ email: '', password: '', firstName: '' });

  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  // Clear any auth errors when modal opens
  React.useEffect(() => {
    if (open) {
      clearError();
    }
  }, [open, clearError]);

  const handleViewChange = (view: AuthModalView, data?: { email?: string; password?: string; firstName?: string }) => {
    setCurrentView(view);
    if (data) {
      setSignupData(prev => ({ ...prev, ...data }));
    }
  };

  const handleClose = () => {
    setCurrentView(initialView);
    setSignupData({ email: '', password: '', firstName: '' });
    onClose();
  };

  const getTitle = () => {
    switch (currentView) {
      case 'signin':
        return 'Welcome Back';
      case 'signup':
        return 'Create Account';
      case 'confirm-signup':
        return 'Verify Email';
      default:
        return 'Authentication';
    }
  };

  const getSubtitle = () => {
    switch (currentView) {
      case 'signin':
        return 'Sign in to access Recruitment Intelligence';
      case 'signup':
        return 'Join to start using AI-powered hiring';
      case 'confirm-signup':
        return 'Check your email for a verification code';
      default:
        return '';
    }
  };

  const renderCurrentView = () => {
    switch (currentView) {
      case 'signin':
        return (
          <SignInForm
            onViewChange={handleViewChange}
            onSuccess={handleClose}
          />
        );
      case 'signup':
        return (
          <SignUpForm
            onViewChange={handleViewChange}
            onSuccess={(email, password, firstName) => {
              setSignupData({ email, password, firstName });
              setCurrentView('confirm-signup');
            }}
          />
        );
      case 'confirm-signup':
        return (
          <ConfirmSignUpForm
            email={signupData.email}
            password={signupData.password}
            firstName={signupData.firstName}
            onViewChange={handleViewChange}
            onSuccess={handleClose}
          />
        );
      default:
        return null;
    }
  };

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      //maxWidth="xs"
      //fullWidth
      fullScreen={isMobile}
      PaperProps={{
        sx: {
          borderRadius: isMobile ? 0 : 3,
          minHeight: isMobile ? 'auto' : 'auto',
          background: 'linear-gradient(145deg, #ffffff 0%, #f8f9fa 100%)',
          width: { xs: '92vw', sm: 420 },
          maxWidth: 'none'
        }
      }}
      TransitionComponent={Fade}
      TransitionProps={{ timeout: 300 }}
    >
      <DialogContent sx={{ p: 0, position: 'relative' }}>
      <IconButton
        onClick={handleClose}
        sx={{
          position: 'absolute',
          right: 16,
          top: 16,
          zIndex: 1,
          width: 40,               // ensure a good hit area
          height: 40,
          borderRadius: '50%',     // force a circle
          backgroundColor: 'rgba(255, 255, 255, 0.9)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          '&:hover': {
            backgroundColor: 'rgba(255, 255, 255, 1)',
          }
        }}
      >
        <Close fontSize="small" />
      </IconButton>

        <Box
          sx={{
            background: 'linear-gradient(135deg, #3f51b5 0%, #2196f3 100%)',
            color: 'white',
            p: 4,
            textAlign: 'center',
            position: 'relative',
            overflow: 'hidden',
          }}
        >
          <Box sx={{ position: 'relative', zIndex: 1 }}>
            <Typography variant="h4" fontWeight={600} gutterBottom>
              {getTitle()}
            </Typography>
            <Typography variant="body1" sx={{ opacity: 0.9 }}>
              {getSubtitle()}
            </Typography>
          </Box>
        </Box>

        <Box sx={{ p: { xs: 2, sm: 4 }}}>
          {renderCurrentView()}
        </Box>
      </DialogContent>
    </Dialog>
  );
};

export default AuthModal;